import { Fragment } from 'react';
import { chatStyles } from '@zonofi/chats-common/styles';
import { MessageVariant } from '@zonofi/chats-common/components/interface';
import { ImageView } from './image-view/image-view';
import {
  theme,
  BannerTypeKeys,
  FileParams,
  TemplateComponentsProps,
  ContactItem,
} from '@zonofi/common';
import { VideoView } from './video-view/video-view';
import { MessageBody } from './message-body';
import { DocumentView } from './document-view';
import { Banner } from './banner';
import { AudioBubble } from './audio-bubble/audio-bubble';
import { Avatar } from '../avatar';
import { ContactView } from './contact-view/contact-view';

export const TemplateComponents = (
  variant: MessageVariant,
  userName?: string
): TemplateComponentsProps => ({
  header: (_: string, text: string) =>
    text.length > 0 ? (
      <div
        key={'header'}
        className={`message-header message-header-${variant} comms-font-size-regular`}
        style={chatStyles.fontSemiBold14Primary}
      >
        {text}
      </div>
    ) : null,
  image: (templateName: string, text: string, file?: FileParams[]) => (
    <Fragment key={'image'}>
      {text.length > 0 && (
        <div
          className={`message-header message-header-${variant} comms-font-size-regular`}
          style={theme.fontConfig.semiBold}
        >
          {text}
        </div>
      )}
      {file?.map((f, index) => (
        <ImageView key={index} {...f} templateName={templateName} />
      ))}
    </Fragment>
  ),
  video: (_: string, text: string, file?: FileParams[]) => {
    return (
      <Fragment key={'video'}>
        {text.length > 0 && (
          <div
            className={`message-header message-header-${variant} comms-font-size-regular`}
            style={theme.fontConfig.semiBold}
          >
            {text}
          </div>
        )}
        {file?.map((f, index) => (
          <VideoView key={index} {...f} />
        ))}
      </Fragment>
    );
  },
  body: (_: string, text: string) => <MessageBody key={'body'} text={text} />,
  footer: (_: string, text: string) => (
    <MessageBody key={'footer'} text={text} />
  ),
  audio: (_x: string, _y: string, file?: FileParams[]) => {
    return (
      <Fragment key={'audio'}>
        {file?.map((f, index) => (
          <AudioBubble key={index} {...f} />
        ))}
      </Fragment>
    );
  },
  document: (templateName: string, text: string, file?: FileParams[]) => (
    <Fragment key={'document'}>
      {text.length > 0 && (
        <div
          className="message-header comms-font-size-medium"
          style={theme.fontConfig.semiBold}
        >
          {text}
        </div>
      )}
      {file?.map((f, index) => (
        <DocumentView
          key={index}
          {...f}
          filename={f.filename || templateName}
        />
      ))}
    </Fragment>
  ),
  banner: (type: string, text: string) => (
    <Banner key={'banner'} type={type as BannerTypeKeys} timetoken={text} />
  ),
  button: (_x: string, _y: string) => null,
  location: (_: string, text: string, locationData?: any) => {
    if (locationData?.thumbnail) {
      const { thumbnail, latitude, longitude } = locationData;
      return (
        <div key="location-thumbnail" className="image-wrapper">
          <img
            className="template-image"
            src={`data:image/jpeg;base64,${thumbnail}`}
            alt="Location"
            style={{
              width: '100%',
              maxWidth: '300px',
              borderRadius: '8px',
              cursor: 'pointer',
            }}
            onClick={() => {
              // Open location in maps if coordinates are available
              if (latitude && longitude) {
                window.open(
                  `https://www.google.com/maps?q=${latitude},${longitude}`,
                  '_blank'
                );
              }
            }}
          />
        </div>
      );
    }
    return <MessageBody key={'location'} text={text} />;
  },
  contact: (_: string, text: string, contactData?: any) => {
    // Handle both single contact and array of contacts
    const contacts: ContactItem[] = Array.isArray(contactData)
      ? contactData
      : contactData
        ? [contactData]
        : [];

    if (contacts.length === 0) {
      return <MessageBody key={'contact'} text={text} />;
    }

    // Single contact - show full card
    if (contacts.length === 1) {
      const { displayName, phoneNumber } = contacts[0];
      return (
        <div
          key="contact-card"
          style={{
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            padding: '12px',
            maxWidth: '300px',
            backgroundColor: '#f9f9f9',
            cursor: 'pointer',
            marginTop: '8px',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
            }}
          >
            <Avatar
              id={phoneNumber || displayName || '-'}
              title={displayName || 'Unknown'}
              variant="user"
              size="medium"
            />
            <div
              style={{
                flex: 1,
                minWidth: 0,
              }}
            >
              <div
                style={{
                  ...chatStyles.fontSemiBold14Primary,
                  marginBottom: '4px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {displayName || 'Unknown Contact'}
              </div>
              {phoneNumber && (
                <div
                  style={{
                    ...chatStyles.fontRegular12,
                    color: theme.text.secondary,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {phoneNumber}
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    // Multiple contacts - show compact view with "View all" button
    return (
      <ContactView
        key="contact-card"
        contacts={contacts}
        userName={userName || ''}
      />
    );
  },
});
